import { ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { Box, Button, Stack, Typography } from '@mui/material'
import type { InterruptRenderProps } from '@copilotkit/react-core/v2'
import { useSnackbar } from '@ui/snackbar'
import {
  NO_FILES_REASON,
  choiceCardOf,
  fileRequestCardOf,
  importPlanCardOf,
  projectChangeCardOf,
} from '../utils'
import ChangeApprovalItem from './ChangeApprovalItem'
import ChoiceItem from './ChoiceItem'
import { ChangeDecision } from './DecisionButtons'
import FloorplanFileRequestItem from './FloorplanFileRequestItem'
import IfcFileRequestItem from './IfcFileRequestItem'
import ImportPlanItem from './ImportPlanItem'

type Props = Pick<InterruptRenderProps, 'interrupts' | 'resolve'>

/**
 * What the agent paused for, rendered into the chat: files it asks for
 * (uploaded right here), its import plan and the changes it wants to make. Each is approved or denied on its own (or all
 * at once); once every change is
 * decided, CopilotKit resumes the agent with the decisions. Only the API
 * turns them into permission to write.
 */
const ChangeApprovalCard = ({ interrupts, resolve }: Props): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const [decisions, setDecisions] = useState<Record<string, ChangeDecision>>({})
  const plans = interrupts.flatMap(interrupt => {
    const plan = importPlanCardOf(interrupt)
    return plan ? [{ id: interrupt.id, plan }] : []
  })
  const changes = interrupts.flatMap(interrupt => {
    const card = projectChangeCardOf(interrupt)
    return card ? [{ id: interrupt.id, card }] : []
  })
  const fileRequests = interrupts.flatMap(interrupt => {
    const card = fileRequestCardOf(interrupt)
    return card ? [{ id: interrupt.id, card }] : []
  })
  const choices = interrupts.flatMap(interrupt => {
    const choice = choiceCardOf(interrupt)
    return choice ? [{ id: interrupt.id, choice }] : []
  })
  if (
    changes.length === 0 &&
    plans.length === 0 &&
    fileRequests.length === 0 &&
    choices.length === 0
  ) {
    return null
  }
  const open = [...plans, ...changes].filter(item => !decisions[item.id])

  const decide = async (ids: string[], approved: boolean, reason?: string) => {
    const decision: ChangeDecision = approved ? 'approved' : 'denied'
    setDecisions(previous => ({
      ...previous,
      ...Object.fromEntries(ids.map(id => [id, decision])),
    }))
    let sent = 0
    try {
      for (const id of ids) {
        await resolve(approved ? { approved } : { approved, reason }, id)
        sent += 1
      }
    } catch (error) {
      // The agent never got these: offer them for a decision again.
      const unsent = new Set(ids.slice(sent))
      setDecisions(previous =>
        Object.fromEntries(Object.entries(previous).filter(([id]) => !unsent.has(id))),
      )
      enqueueSnackbar(
        buildErrorMessage(error as Error, 'KI-Chat: Entscheidung konnte nicht gesendet werden'),
        { variant: 'error' },
      )
    }
  }

  return (
    <Box
      sx={{ border: 1, borderColor: 'warning.main', borderRadius: 1, p: 1, my: 1 }}
      data-cy="ai-chat-change-approval"
    >
      <Typography variant="subtitle2" mb={1}>
        {choices.length > 0 && plans.length === 0 && changes.length === 0
          ? 'Der Assistent braucht eine Entscheidung'
          : fileRequests.length > 0 && plans.length === 0 && changes.length === 0
          ? 'Der Assistent benötigt Dateien'
          : plans.length > 0
          ? 'Der Assistent bittet um Freigabe seines Importplans'
          : changes.length === 1
          ? 'Der Assistent möchte das Projekt ändern'
          : `Der Assistent möchte ${changes.length} Änderungen am Projekt vornehmen`}
      </Typography>
      <Stack spacing={1}>
        {choices.map(({ id, choice }) => (
          <ChoiceItem
            key={id}
            choice={choice}
            decision={decisions[id]}
            onDecide={approved => decide([id], approved, approved ? undefined : choice.denyReason)}
          />
        ))}
        {fileRequests.map(({ id, card }) => {
          const onDecide = (uploaded: boolean) =>
            decide([id], uploaded, uploaded ? undefined : NO_FILES_REASON)
          return card.fileKind === 'ifc' ? (
            <IfcFileRequestItem
              key={id}
              message={card.message}
              decision={decisions[id]}
              onDecide={onDecide}
            />
          ) : (
            <FloorplanFileRequestItem
              key={id}
              card={card}
              decision={decisions[id]}
              onDecide={onDecide}
            />
          )
        })}
        {plans.map(item => (
          <ImportPlanItem
            key={item.id}
            plan={item.plan}
            decision={decisions[item.id]}
            onDecide={approved => decide([item.id], approved)}
          />
        ))}
        {changes.map(change => (
          <ChangeApprovalItem
            key={change.id}
            card={change.card}
            decision={decisions[change.id]}
            onDecide={approved => decide([change.id], approved)}
          />
        ))}
      </Stack>
      {open.length > 1 && (
        <Stack direction="row" spacing={1} mt={1} justifyContent="flex-end">
          <Button
            size="small"
            color="inherit"
            onClick={() =>
              decide(
                open.map(change => change.id),
                false,
              )
            }
            data-cy="ai-chat-change-deny-all"
          >
            Alle ablehnen
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              decide(
                open.map(change => change.id),
                true,
              )
            }
            data-cy="ai-chat-change-approve-all"
          >
            Alle freigeben
          </Button>
        </Stack>
      )}
    </Box>
  )
}

export default ChangeApprovalCard
